import React from 'react';
import { Link } from 'react-router-dom';
import {
  Mail, Phone, ShieldCheck, KeyRound, Stethoscope, ArrowRight,
  Briefcase, MapPin, BadgeCheck, CheckCircle2,
} from 'lucide-react';

/**
 * GetStartedPage — public 1-pager explaining how to register as a doctor
 * on DocSite. No dashboard screenshots, no feature previews — competitors
 * see nothing useful here. The intent is purely to (a) qualify the doctor
 * and (b) walk them through the 4 mandatory signup fields BEFORE they
 * land on the form so they don't bounce.
 *
 * Route: /get-started (public, but indexable as a top-of-funnel page).
 */
const STEPS = [
  {
    n: 1, icon: Mail,
    title: 'Enter your basics + practice details',
    body: (
      <>Name, email, mobile number, and four mandatory practice details:
        <ul className="mt-1 text-[12px] text-slate-600 list-none space-y-1">
          <li className="flex items-start gap-1.5"><Briefcase size={11} className="text-sky-600 mt-0.5" /> Years of practice</li>
          <li className="flex items-start gap-1.5"><BadgeCheck size={11} className="text-sky-600 mt-0.5" /> NMC / State Medical Council registration number</li>
          <li className="flex items-start gap-1.5"><MapPin size={11} className="text-sky-600 mt-0.5" /> City of practice</li>
          <li className="flex items-start gap-1.5"><Stethoscope size={11} className="text-sky-600 mt-0.5" /> Primary specialisation</li>
        </ul>
      </>
    ),
  },
  {
    n: 2, icon: KeyRound,
    title: 'Create your password',
    body: 'At least 8 characters with 1 uppercase + 1 number. You can change it later from your dashboard.',
  },
  {
    n: 3, icon: Phone,
    title: 'Verify mobile via OTP',
    body: 'We text a 4-digit code to your number. Enter it on the form. Takes < 30 seconds.',
  },
  {
    n: 4, icon: ShieldCheck,
    title: 'Sign in & start setup',
    body: 'After registration, log in with email + password. The dashboard opens with a 11-minute guided wizard to publish your profile.',
  },
];

const WHATS_NEEDED = [
  'Active NMC / State Medical Council registration number',
  'Mobile number that can receive SMS for OTP verification',
  'Email address you check regularly (used for password reset + notifications)',
  '2 minutes — no documents to upload at this stage',
];

const ELIGIBILITY = [
  'MBBS + post-graduate / super-specialty doctors',
  'AYUSH practitioners (BAMS, BHMS) with authorised qualifications',
  'BDS dentists with State Dental Council registration',
];

export default function GetStartedPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Hero */}
      <header className="max-w-3xl mx-auto px-5 pt-10 pb-6 text-center">
        <p className="text-[11px] uppercase tracking-[0.22em] text-sky-600 font-bold">For Registered Medical Practitioners</p>
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Create your DocSite account
        </h1>
        <p className="text-sm text-slate-600 mt-3 max-w-xl mx-auto leading-relaxed">
          A 2-minute signup that gets you your own SEO-optimised doctor website on the
          forever-free Gold plan. No credit card. Cancel any time.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/register?as=doctor" data-testid="get-started-cta"
            className="inline-flex items-center gap-1.5 rounded-full px-6 py-3 bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 transition-colors shadow-sm">
            Start signup <ArrowRight size={14} />
          </Link>
          <Link to="/login" data-testid="get-started-login"
            className="inline-flex items-center gap-1.5 rounded-full px-5 py-3 bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50">
            Already registered? Sign in
          </Link>
        </div>
      </header>

      {/* Steps */}
      <main className="max-w-3xl mx-auto px-4 pb-14">
        <h2 className="text-lg font-bold text-slate-900 mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          How registration works
        </h2>
        <ol className="space-y-3">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <li key={s.n} className="flex items-start gap-3 bg-white rounded-2xl border border-slate-200 p-4 shadow-sm"
                data-testid={`get-started-step-${s.n}`}>
                <div className="w-9 h-9 rounded-xl bg-sky-50 border border-sky-200 flex items-center justify-center shrink-0">
                  <Icon size={16} className="text-sky-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Step {s.n}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-900">{s.title}</p>
                  <div className="text-[13px] text-slate-600 mt-1 leading-relaxed">{s.body}</div>
                </div>
              </li>
            );
          })}
        </ol>

        {/* What to keep ready */}
        <section className="mt-8 grid md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5">
            <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-widest mb-2">Keep ready before you start</p>
            <ul className="space-y-1.5">
              {WHATS_NEEDED.map((it, i) => (
                <li key={i} className="text-[13px] text-slate-700 flex items-start gap-2">
                  <CheckCircle2 size={13} className="text-emerald-600 mt-0.5 shrink-0" /> <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-sky-200 bg-sky-50/40 p-5">
            <p className="text-[11px] font-bold text-sky-700 uppercase tracking-widest mb-2">Who can register</p>
            <ul className="space-y-1.5">
              {ELIGIBILITY.map((it, i) => (
                <li key={i} className="text-[13px] text-slate-700 flex items-start gap-2">
                  <ShieldCheck size={13} className="text-sky-600 mt-0.5 shrink-0" /> <span>{it}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-slate-500 mt-3">
              Wrong registration IDs are auto-terminated. Make sure your number is active.
            </p>
          </div>
        </section>

        {/* What's behind the wall */}
        <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm font-semibold text-slate-900 mb-1.5">After you sign in, the dashboard opens with:</p>
          <p className="text-[13px] text-slate-600 leading-relaxed">
            A guided 5-step wizard (~11 minutes), a printable reference, a screen-by-screen
            visual walkthrough, your public profile preview, and SEO health checks. We
            keep these behind login so only registered doctors see them.
          </p>
        </section>

        {/* Footer CTA */}
        <div className="mt-10 flex flex-col items-center gap-2.5">
          <Link to="/register?as=doctor"
            className="inline-flex items-center gap-1.5 rounded-full px-6 py-3 bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700"
            data-testid="get-started-footer-cta">
            Start signup now <ArrowRight size={14} />
          </Link>
          <p className="text-[11px] text-slate-400">2 minutes. Free forever.</p>
        </div>
      </main>
    </div>
  );
}
